import './Form.scss';
import { Container } from '@components/Component/Container/Container.js';
import { DataRecord } from '@components/DataRecord/DataRecord.js';
import { paramsFromUrlGet } from '@components/Utility/UrlParser.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { containerAutoResize, debounce, deepClone, forEachKey, isArray, mapCancelToFalse } from '@components/Utility/index.js';
import { asapScheduler, combineLatest, concat, filter, map, Observable, observeOn, of, Subject, takeUntil, tap, toArray } from 'rxjs';
import { DataDict } from '@components/DataDict.js';
import { Modal } from '@components/Component/Container/Modal/Modal.js';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { ENV, TOKEN } from '@components/Environment.js';
import { RecordStore } from '@components/RecordStore/RecordStore.js';
import { FilterPanel } from '@components/Component/Container/Panel/FilterPanel/FilterPanel.js';
/**
 * `Form` manages the components and records of forms in Q360 similar to the Footer component in the old framework.
 * It extends the `Container` class and provides methods to manage and interact with a form.
 * It is expected that this would be extended by the specific form you are looking at, ie. class CustomerForm extends Form {}
 */
export class Form extends Container {
    constructor() {
        super(...arguments);
        this.componentName = 'Form';
        this.initialLoadDone = false;
        this.lastParameters = {};
        this.routes = [];
        this.modals = {};
        this.suppressedMandatoryIds = new Set();
        this.recordLoadColumnMapping = {};
        this.cancelRefreshData$ = new Subject();
        this._parameters = {};
        this.formNo = '';
        this._formName = '';
        this.routeFormName = '';
        this._titleBase = '';
        this._titleView = '';
        this._titleAdd = '';
        /**
         * Only applicable if a closePromptCaptionKey is set. If it's not set, this property is ignored regardless of the value.
         * If this property is FALSE, the prompt that appears will be a confirm which allows the user to still close
         * or if this property is TRUE, the prompt that appears will be an alert which prevents the user from closing.
         * @private
         */
        this._blockClose = false;
        /**
         * Whether or not refreshData should load from the database or just expect the parameters to be the "data"
         * @protected
         */
        this.loadExternalData = true;
        /**
         * these are only expected to come from loading a form definition for now.
         * If needed in a form class such as quoteform.js or something, just use this.broadcastListen() or bring it up for discussion
         * @protected
         */
        this._broadcastListenerConditions = {};
        this.userPreferences = {
            components: {}
        };
    }
    // Expose only the underlying records as a plain object.
    get records() {
        return this.recordStore?.recordsGet() || {};
    }
    get ConditionsManager() {
        return DIContainer.getInstance('ConditionsManager');
    }
    get abstractFlag() { return this._abstractFlag; }
    set abstractFlag(s) {
        this._propertySet('abstractFlag', s);
    }
    get routeOnlyFlag() { return this._routeOnlyFlag; }
    set routeOnlyFlag(s) {
        this._propertySet('routeOnlyFlag', s);
    }
    get parameters() { return this._parameters; }
    set parameters(value) { this._propertySet('parameters', value); }
    get formName() { return this._formName; }
    set formName(value) { this._propertySet('formName', value); }
    get title() { return this._title || ''; }
    set title(value) { this._propertySet('title', value); }
    get titleBase() { return this._titleBase; }
    set titleBase(t) {
        this._propertySet('titleBase', t);
    }
    get titleView() { return this._titleView; }
    set titleView(t) {
        this._propertySet('titleView', t);
    }
    get titleAdd() { return this._titleAdd; }
    set titleAdd(t) {
        this._propertySet('titleAdd', t);
    }
    get hasUnSavedChanges() {
        return (this.record?.recordType === 'primary' && ['edit', 'add'].indexOf((this.recordState ?? '')) > -1);
    }
    get closePromptCaptionKey() {
        return this._closePromptCaptionKey;
    }
    set closePromptCaptionKey(value) {
        this._propertySet('closePromptCaptionKey', value);
    }
    get blockClose() {
        return this._blockClose;
    }
    set blockClose(value) {
        this._propertySet('blockClose', value);
    }
    screenRootMoved() {
        if (this._screenRootMoved) {
            this._screenRootMoved.next();
        }
    }
    get broadcastListenerConditions() { return this._broadcastListenerConditions; }
    set broadcastListenerConditions(val) {
        forEachKey(this._broadcastListenerConditions, (value, key) => {
            delete this.dynamicCodeEvents[key];
        });
        this._propertySet('broadcastListenerConditions', val);
        forEachKey(val, (value, key) => {
            if (key) {
                this.dynamicCodeEvents[key] = 'event';
            }
        });
    }
    // constructor(id: string, rootElement?: HTMLElement | null, dependencies: ComponentDependenciesInterface = {}) {
    // 	super(id, rootElement, dependencies);
    //
    //
    // }
    init() {
        super.init();
        this.recordStore = new RecordStore();
        this._screenRootMoved = new Subject();
        const self = this;
        this.formContext = {
            get formNo() {
                return self.formNo;
            },
            get formName() {
                return self.formName;
            },
            get formType() {
                return self.propertyGet('formType') || 'Q360Form';
            },
            get parameters() {
                return { ...self.parameters };
            },
            handleResize: self.handleResize.bind(self),
            debouncedHandleResize: debounce(self.handleResize.bind(self), 10),
            refreshData: self.refreshData.bind(self),
            componentGet: self.componentGet.bind(self),
            componentsGet: self.componentsGet.bind(self),
            componentGet$: self.componentGet$.bind(self),
            globalId: this.globalId,
            getCurrentUrl: self.getCurrentUrl.bind(self),
            saveUserPreference: self.saveUserPreference.bind(self),
            getUserPreference: self.getUserPreference.bind(self),
            broadcastSend: self.broadcastSend.bind(self),
            screenRootMoved$: this._screenRootMoved.pipe(takeUntil(this.destroy$))
        };
    }
    getUserPreference(componentId, property) {
        return this.userPreferences.components[componentId]?.[property];
    }
    saveUserPreference(componentId, property, value) {
        if (!this.userPreferences.components[componentId]) {
            this.userPreferences.components[componentId] = {};
        }
        this.userPreferences.components[componentId][property] = value;
        return this.Request.PATCH({
            endpoint: 'form',
            action: 'preferenceSave',
            showErrors: true,
            requestData: {
                properties: JSON.stringify(this.userPreferences),
                formname: this.formName
            },
            initiator: this
        }).pipe(tap(() => {
            DIContainer.getInstance('Request').clearCache({
                endpoint: `/form/${this.formName}`,
                requestData: {},
                cache: true
            }).subscribe();
        }));
    }
    draw() {
        this.rootElement.classList.add('s360Form');
        super.draw();
        this.setupBroadcastListeners();
    }
    setupBroadcastListeners() {
        forEachKey(this.broadcastListenerConditions, (value, key) => {
            this.broadcastListen(key).subscribe((broadcast) => {
                this.trigger(key, {
                    defaultEventHandler: (broadcast) => {
                        console.warn(`A broadcasted event ${key} was received but no handler was defined.`, broadcast);
                    }
                }, broadcast);
            });
        });
    }
    broadcastListen(eventName) {
        return DIContainer.getInstance('BroadcastManager').listen(eventName).pipe(takeUntil(this.destroy$), filter((broadcast) => {
            return this.validateBroadcastPayload(eventName, broadcast);
        }));
    }
    validateBroadcastPayload(eventName, broadcast) {
        const broadCastConditions = this.broadcastListenerConditions[eventName];
        if (!broadCastConditions.includeself && broadcast.initiatorId === this.globalId) {
            return false; // dont handle events sent by this form
        }
        if (!broadCastConditions || !broadCastConditions.condition) {
            return true;
        }
        let conditionPass = false;
        const check = (data) => {
            return DIContainer.getInstance('ConditionsManager')
                .validateConditions(broadCastConditions.condition, {
                initiator: this,
                form: this,
                tokenData: data,
                record: this.record
            });
        };
        if (isArray(broadcast.payload)) {
            let i = 0;
            while (!conditionPass && i < broadcast.payload.length) {
                conditionPass = check(broadcast.payload[i]);
                i++;
            }
        }
        else {
            conditionPass = check(broadcast.payload);
        }
        return conditionPass;
    }
    broadcastSend(eventName, broadcast) {
        broadcast.initiatorId = this.globalId;
        DIContainer.getInstance('BroadcastManager').send(eventName, broadcast);
    }
    /**
     * Request getter which handles lazy loading the `Request` dependency. Supports injection for unit tests etc.
     */
    get Request() {
        if (!this.dependencies.Request) {
            // we didnt receive a request dependency injection, so just use the global singleton
            this.dependencies.Request = DIContainer.getInstance('Request');
        }
        return this.dependencies.Request;
    }
    captionTypesSet() {
        this.captionTypes = {
            title: 'titleBase',
            titleadd: 'titleAdd',
            titleview: 'titleView'
        };
        this.updateTitle();
    }
    requireState(states, alert = true) {
        if (states.indexOf(this.recordState) === -1) {
            if (alert) {
                this.alert({
                    captionKey: 'q360_extendedmenu_invalidstate',
                    captionKeyTokens: {
                        enabledstates: states.join(' or '),
                        objecttype: 'Form',
                        currentstate: this.recordState
                    }
                });
            }
            return false;
        }
        return true;
    }
    /**
     * This function is only intended to be called once per instance of Form when it initially loads the form response from database
     * This runs before the form draws.
     * @param oDefinition - FormDefinition Interface. The payload from the /api/form endpoint
     */
    loadFormDefinition(oDefinition) {
        this.routes = oDefinition.q360formroute;
        if (oDefinition.form.userPreferences) {
            this.userPreferences = JSON.parse(oDefinition.form.userPreferences);
        }
        if (this.formDesignerMode) {
            this.defaultPropertiesObject = this.formDesignerPropertiesGet({});
            // on load, before we set properties for the form, store the initial values (ie. might come from QuoteForm.js)
            // then when saving, if the form designer overrode the value in the code then we will save it. 
            forEachKey(this.defaultPropertiesObject, (prop, key) => {
                const saveFlag = typeof prop.saveFlag === 'function' ? prop.saveFlag() : prop.saveFlag;
                if (!saveFlag) {
                    delete this.defaultPropertiesObject[key];
                }
            });
            if (this.inheritedProperties?.form?.properties) {
                forEachKey(this.inheritedProperties.form.properties, (value, key) => {
                    delete this.defaultPropertiesObject[key];
                });
            }
        }
        if (oDefinition.inheritedProperties) {
            this.inheritedProperties = oDefinition.inheritedProperties;
        }
        else {
            delete this.inheritedProperties;
        }
        if (oDefinition.form.q360FormNo) {
            this.formNo = oDefinition.form.q360FormNo;
        }
        if (oDefinition.form.formName && (!this._formName || this._formName === this.formNo)) {
            // only set the formname when its empty or if the form was loaded using a pk, to preserve the original URL used to load the form.
            this.formName = oDefinition.form.formName;
        }
        this.routeFormName = (oDefinition.form.formName || this.formName).replace('custom_', ''); // the actual view we loaded, ie we might be at /form/dispatch but loaded external_dispatch
        if (oDefinition.form.properties) {
            if (typeof oDefinition.form.properties === 'string') {
                oDefinition.form.properties = JSON.parse(oDefinition.form.properties);
            }
            forEachKey(oDefinition.form.properties, (val, key) => {
                this.propertySet(key, val);
            });
        }
        this.viewCondition = oDefinition.form?.condition;
        this.parentFormName = oDefinition.form?.parentFormName;
        this.abstractFlag = oDefinition.form?.abstractFlag;
        this.routeOnlyFlag = oDefinition.form?.routeOnlyFlag;
        if (oDefinition.datadict) {
            oDefinition.datadict.forEach((dd) => {
                if (!Reflect.has(DataDict, dd.table_dbf)) {
                    DataDict[dd.table_dbf] = {};
                }
                DataDict[dd.table_dbf][dd.field_name] = dd;
            });
            console.log(DataDict);
        }
        const captionManager = this.CaptionManager;
        captionManager.addCaptions(oDefinition.captions);
        if (!this.CaptionManager.hasKey(oDefinition.form.captionKey)) {
            this.CaptionManager.addKey(oDefinition.form.captionKey);
        }
        this.captionKey = oDefinition.form.captionKey;
        const detailComponents = {};
        const detailSources = {};
        const secondaryRecords = [];
        oDefinition.records.forEach((recordDef) => {
            // if (this.formDesignerMode) {
            oDefinition.q360formrecordvalue = oDefinition.q360formrecordvalue || [];
            const filteredValues = oDefinition.q360formrecordvalue.filter((obj) => obj.recordname === recordDef.recordName);
            recordDef.q360formrecordvalue = filteredValues;
            // }
            const record = this.recordSetup(recordDef);
            if (record.recordType === 'detail') {
                detailComponents[record.id] = [];
            }
            if (record.recordType === 'secondary' && record.defaultState === 'none') {
                secondaryRecords.push(record);
            }
        });
        if (!this.records.main) {
            this.recordSetup({ recordName: 'main', state: 'none', recordType: 'primary' });
        }
        if (secondaryRecords.length) {
            this.records.main.propertyGet$('state').pipe(map((state) => {
                return state === 'edit';
            }), distinctUntilChanged()).subscribe((isEdit) => {
                secondaryRecords.forEach((record) => {
                    record.state = record.state === 'none' ? 'none' : (isEdit ? 'edit' : 'read');
                });
            });
        }
        this.recordLoadColumnMapping = {};
        oDefinition.records.forEach((recordDef) => {
            if (recordDef.q360formrecordvalue) {
                recordDef.q360formrecordvalue.forEach((valueDefinition) => {
                    const regex = /\$\{(.*?)}\$/g;
                    let match;
                    // if we can detect a ${main.customerno}$ (or any token pattern like ${something}$)
                    while ((match = regex.exec(valueDefinition.defaultvalue)) !== null) {
                        // split the matched value
                        const splitChars = match[1].split('.');
                        const recordName = splitChars.length > 1 ? splitChars[0] : 'main'; // extract the first word inside brackets. if there is only one word, hardcode this one to "main"
                        const columnName = splitChars.length > 1 ? splitChars[1] : splitChars[0]; // extract the second word inside brackets. if there is only one word, use it instead.
                        if (!this.recordLoadColumnMapping[recordDef.recordName]) {
                            this.recordLoadColumnMapping[recordDef.recordName] = [];
                        }
                        this.recordLoadColumnMapping[recordDef.recordName].push({
                            columnName: valueDefinition.columnname,
                            fromColumnName: columnName,
                            recordName
                        });
                        // this will ensure if a synced value changes during edit mode, it will set the value in our record.
                        // we handle the initial "on load" value elsewhere.
                        const record = this.records[recordDef.recordName];
                        const options = { ignoreSource: ['load'] };
                        if (this.records[recordName].recordType === 'detail') {
                            options.ignoreSource.pop();
                        }
                        this.records[recordName]?.get$(columnName, options)
                            .subscribe((newValue) => {
                            record.set(valueDefinition.columnname, newValue);
                        });
                    }
                });
            }
        });
        /*const createComponentObservable = (componentDef: any, delayTime: number = 0) => {
            let observable = this.componentSetup(componentDef).pipe(
                // catchError(err => {
                // 	console.error('Error during loadFormDefinition:', err);
                // 	return of(undefined);
                // }),
                tap((component) => {
                    if (component) {
                        if (componentDef.recordName && detailComponents[componentDef.recordName]) {
                            detailComponents[componentDef.recordName].push(component);
                        }

                        if (componentDef.detailRecordName) {
                            const detailRecord = this.records[componentDef.detailRecordName];
                            component.propertySet('detailRecord', detailRecord);
                            detailSources[detailRecord.id] = component;
                        }
                    }
                })
            );

            if (delayTime > 0) {
                observable = observable.pipe(delay(delayTime));
            }

            return observable;
        };*/
        const componentObservables = [];
        oDefinition.components.forEach((componentDef) => {
            /*if (index % 100 === 0) {
                componentObservables.push(createComponentObservable(componentDef, 1));
            } else {
                componentObservables.push(createComponentObservable(componentDef));
            }*/
            componentObservables.push(this.componentSetup(componentDef).pipe(tap((component) => {
                if (component) {
                    if (componentDef.recordName && detailComponents[componentDef.recordName]) {
                        detailComponents[componentDef.recordName].push(component);
                    }
                    if (componentDef.detailRecordName) {
                        const detailRecord = this.records[componentDef.detailRecordName];
                        component.propertySet('detailRecord', detailRecord);
                        detailSources[detailRecord.id] = component;
                    }
                }
            })));
        });
        oDefinition.suppressedmandatoryfields?.forEach((componentDef) => {
            componentObservables.push(this.componentSetup(componentDef).pipe(tap((component) => {
                if (component) {
                    this.suppressedMandatoryIds.add(component.id);
                }
            })));
        });
        function isDefined(value) {
            return value !== undefined;
        }
        return concat(...componentObservables)
            .pipe(filter((isDefined)), toArray(), tap(() => {
            forEachKey(detailSources, (component, detailRecordId) => {
                const components = detailComponents[detailRecordId];
                if (typeof component.linkComponents === 'function') {
                    component.linkComponents(components);
                }
            });
            oDefinition.helpanchors?.forEach((anchor) => {
                const component = this.componentGet(anchor.componentid);
                if (!component) {
                    return;
                }
                if (anchor.linkno) {
                    if (this.records.main) {
                        this.records.main.get$(this.records.main.pkColumnName)
                            .subscribe((pk) => {
                            pk = pk || '';
                            if (anchor.linkno.toLowerCase() === pk.toLowerCase()) {
                                component.helpAnchor = anchor;
                            }
                            else if (component.helpAnchor === anchor) {
                                component.helpAnchor = undefined;
                            }
                        });
                    }
                }
                else {
                    component.helpAnchor = anchor;
                }
            });
            forEachKey(this.records, (record) => {
                this.businessLogicHook('record', record);
            });
            forEachKey(this.components, (component) => {
                this.businessLogicHook('component', component);
            });
            if (this.records.main) {
                this.linkRecord(this.records.main);
            }
            // re-run this now that our recordStore is updated, it will observe the caption tokens
            this.captionsLoad();
        }));
    }
    updateTitle() {
        this.titleSub?.unsubscribe();
        this.titleSub = combineLatest([
            this.propertyGet$('titleBase'),
            this.propertyGet$('titleAdd'),
            this.propertyGet$('titleView')
        ]).subscribe(([titleBase, titleAdd, titleView]) => {
            let title = titleBase;
            if (this.records.main) {
                if (this.records.main.state === 'add') {
                    title = titleAdd ? titleAdd : (titleView ? titleView : title);
                }
                else if (['edit', 'read'].indexOf(this.records.main.state) > -1) {
                    title = titleView ? titleView : title;
                }
            }
            if (title && this._title !== title) {
                this.title = title;
            }
        });
        // console.log('update title');
        // // let template = '';
        //
        // const titleAdd = this.CaptionManager.getCaption(`${this.captionKey}_titleadd`, this.records.main?.data, {records: this.recordStore.recordsGet()})
        // const titleView = this.CaptionManager.getCaption(`${this.captionKey}_titleview`, this.records.main?.data, {records: this.recordStore.recordsGet()})
        // const titleBase = this.CaptionManager.getCaption(`${this.captionKey}_title`, this.records.main?.data, {records: this.recordStore.recordsGet()})
        //
        // let currentProperty = (titleBase || this.CaptionManager.hasKey(`${this.captionKey}_title`)) ? 'titleBase' : '';
        // let template = titleBase;
        // if (this.records.main) {
        // 	if (this.records.main.state === 'add') {
        // 		template = titleAdd ? titleAdd : titleView;
        // 		currentProperty = titleAdd ? 'titleAdd' : (titleView ? 'titleView' : currentProperty);
        // 	} else if (['edit', 'read'].indexOf(this.records.main.state) >- 1) {
        // 		template = titleView;
        // 		currentProperty = titleView ? 'titleView' : currentProperty;
        // 	}
        // }
        //
        // this.titleSub?.unsubscribe();
        //
        // if (!template) {
        // 	template = titleBase;
        // }
        //
        // if (currentProperty) {
        // 	this.title = template;
        // 	this.titleSub = this.propertyGet$(currentProperty).subscribe((val) => {
        // 		if (this._title !== val) {
        // 			this.title = val;
        // 		}
        // 	});
        // }
        // if (!template) {
        // 	template = titleBase;
        // }
        // if (template || this.CaptionManager.hasKey(`${this.captionKey}_title`)) {
        // 	// dont attempt to update the title to empty if the caption key didnt exist
        // 	if (this._title !== template) {
        // 		this.title = template;
        // 	}
        // }
    }
    /**
     * Hook for form level code to setup record or components before the form draws.
     * ie. create Observables etc.
     * @param type
     * @param item
     *
     * @example
     * record_recordname(record) {}
     * component_componentid(component) {}
     *
     *
     */
    businessLogicHook(type, item) {
        if (typeof this[`${type}_${item.id}`] === 'function') {
            // call form specific component setup code defined in CustomerForm.js etc 
            this[`${type}_${item.id}`](item);
        }
    }
    // private componentSubjects: Map<string, ReplaySubject<Component>> = new Map();
    // componentGet$<T extends Component>(id: string): Observable<T> {
    // 	let subject = this.componentSubjects.get(id);
    // 	if (!subject) {
    // 		subject = new ReplaySubject<Component>(1);
    // 		this.componentSubjects.set(id, subject);
    //
    // 		const comp = this.componentGet(id);
    // 		if (comp !== undefined) {
    // 			subject.next(comp);
    // 		}
    // 	}
    //
    // 	return (subject.asObservable() as Observable<T>).pipe(take(1));
    // }
    componentDependenciesObjectGet() {
        return {
            formDesignerMode: this.formDesignerMode,
            recordStore: this.recordStore,
            formContext: this.formContext
        };
    }
    /**
     * Loads component properties, connects it to record as applicable, and calls form-specific setup function if exists
     * @param componentDef
     * @private
     */
    componentSetup(componentDef) {
        return of(componentDef).pipe(switchMap((componentDef) => {
            if (this.componentGet(componentDef.componentId)) {
                return of(this.componentGet(componentDef.componentId));
            }
            return DIContainer.resolveLazy(componentDef.componentType, componentDef.componentId, undefined, {
                formDesignerMode: this.formDesignerMode,
                recordStore: this.recordStore,
                formContext: this.formContext
                // formContext: `this` works too, but creates a circular reference. While not technically an issue, 
                // it might be confusing in debugging and risks people getting a bit carried away with 
                // tightly coupling form and components. Trying to keep the injected interface minimal.
            });
        }), observeOn(asapScheduler), tap((component) => {
            if (!component) {
                return new Observable(subscriber => {
                    subscriber.error(new Error(`${componentDef.componentType} is not a valid component type.`));
                });
            }
            if (component.compositeChild) {
                componentDef.columnName = '';
                componentDef.displayColumnName = '';
                // compsite child components that are part of the framework must remain in their original container
                componentDef.containerId = component.containerId || componentDef.containerId;
            }
            else {
                component.containerId = componentDef.containerId;
            }
            component.q360FormComponentId = (componentDef.q360FormComponentId || '');
            component.seq = Number(componentDef.seq) || 0;
            component.inheritProperties = componentDef.inheritProperties;
            // component.formDesignerMode = this.formDesignerMode;
            if (this.formDesignerMode) {
                component.defaultPropertiesObject = deepClone(component.formDesignerPropertiesGet({}));
                forEachKey(component.defaultPropertiesObject, (prop, key) => {
                    const saveFlag = typeof prop.saveFlag === 'function' ? prop.saveFlag() : prop.saveFlag;
                    if (!saveFlag) {
                        delete prop[key];
                    }
                });
            }
            let prop;
            if (componentDef.properties) {
                prop = typeof componentDef.properties === 'string' ? JSON.parse(componentDef.properties) : componentDef.properties;
            }
            // set our columns before linking records
            if (componentDef.columnName) {
                component.propertySet('columnName', componentDef.columnName);
            }
            if (prop && prop.displayColumnName) {
                component.propertySet('displayColumnName', prop.displayColumnName);
            }
            if (component instanceof Modal) {
                // modals draw themselves on first open, and dont belong in a form/containers child template so we can just manually assign it a rootElement and attach it to the form.
                component.destroyOnSubmit = false;
                component.ignoreChildTemplate = true;
                if (!(component instanceof FilterPanel)) {
                    this.modals[component.id] = component; // TODO: might not need this.
                }
            }
            if (prop && prop.modalClassName && this.modals[prop.modalClassName]) {
                prop.modal = this.modals[prop.modalClassName];
            }
            component.comments = componentDef.comments;
            if (componentDef.viewCondition) {
                component.viewCondition = componentDef.viewCondition;
                const componentViewCondition = JSON.parse(componentDef.viewCondition);
                this.setupDynamicComponentCondition(component, componentViewCondition, 'hidden', componentDef.recordName);
            }
            if (prop && prop.buttons && prop.buttons.length) {
                const dataObservables = [];
                const addObservable = (definition) => {
                    if (definition.column) {
                        const record = this.recordGet(definition.recordName || componentDef.recordName || 'main');
                        dataObservables.push(record.get$(definition.column, { distinct: true }).pipe(startWith('')));
                    }
                    else if (definition.token) {
                        dataObservables.push(of(TOKEN(definition.token)));
                    }
                };
                prop.buttons.forEach((buttonDef) => {
                    if (buttonDef.viewCondition) {
                        const buttonViewCondition = JSON.parse(buttonDef.viewCondition);
                        buttonViewCondition.conditions.forEach((condition) => {
                            if (condition.data) {
                                condition.data.forEach((definition) => {
                                    if (isArray(definition)) {
                                        definition.forEach((d2) => addObservable(d2));
                                    }
                                    else {
                                        addObservable(definition);
                                    }
                                });
                            }
                        });
                    }
                });
                if (dataObservables.length) {
                    combineLatest(dataObservables)
                        .pipe(takeUntil(component.destroy$), debounceTime(dataObservables.length > 1 ? 10 : 0)).subscribe(() => {
                        component.renderButtons();
                    });
                }
            }
            // need to set this before recordName, because otherwise recordName is the "default" parameter record if this property is not supplied.
            if (componentDef.parameterRecordName) {
                component.propertySet('parameterRecord', this.records[componentDef.parameterRecordName]);
            }
            if (componentDef.recordName) {
                component.propertySet('recordName', componentDef.recordName);
                if (!this.records[componentDef.recordName]) {
                    console.warn(`Record: ${componentDef.recordName} is missing from form definition response but requested for component: ${componentDef.componentId}`);
                    this.records[componentDef.recordName] = new DataRecord(componentDef.recordName, { formGlobalId: this.globalId }); // avoid fatal crash
                }
                component.linkRecord(this.records[componentDef.recordName]);
            }
            if (componentDef.modalRecordName) {
                component.propertySet('modalRecord', this.records[componentDef.modalRecordName]);
            }
            // if (componentDef.recordName && typeof (component as any).linkRecord === 'function') {
            // 	(component as any).linkRecord(this.records[componentDef.recordName]);
            // }
            if (componentDef.captionKey) {
                if (!this.formDesignerMode && !this.CaptionManager.hasKey(componentDef.captionKey)) {
                    this.CaptionManager.addKey(componentDef.captionKey);
                }
                component.captionKey = componentDef.captionKey;
            }
            if (!this.formDesignerMode && this.userPreferences.components[component.id]) {
                prop = {
                    ...(prop || {}),
                    ...this.userPreferences.components[component.id]
                };
            }
            if (prop) {
                component.propertiesLoad(prop);
            }
            if (prop?.enabledCondition) {
                const componentEnabledCondition = JSON.parse(prop.enabledCondition);
                this.setupDynamicComponentCondition(component, componentEnabledCondition, 'disabled', componentDef.recordName);
            }
            if (component.compositeChild) {
                // already added to its respective containers
                return;
            }
            if (component.containerId && this.components[componentDef.containerId]) {
                // container will draw its children when applicable
                this.components[componentDef.containerId].componentAdd(component, this.components[componentDef.containerId].drawn);
            }
            if (component instanceof Container && component.compositeContainer) {
                this.addCompositeComponent(component);
            }
            this.componentAdd(component);
            //
            // const subject = this.componentSubjects.get(component.id);
            // if (subject) {
            // 	subject.next(component);
            // }
        }));
    }
    setupDynamicComponentCondition(component, componentConditions, property, recordName) {
        const dataObservables = [];
        let envCondition = false;
        let configCondition = false;
        const addObservable = (definition) => {
            if (definition.column) {
                const record = this.recordGet(definition.recordName || recordName || 'main');
                dataObservables.push(record.get$(definition.column, { distinct: true }).pipe(startWith('')));
            }
            else if (definition.token) {
                dataObservables.push(of(TOKEN(definition.token)));
            }
        };
        if (typeof componentConditions !== 'boolean') {
            componentConditions.conditions.forEach((condition) => {
                if (condition.env) {
                    envCondition = true;
                }
                if (condition.config) {
                    configCondition = true;
                }
                if (condition.data) {
                    condition.data.forEach((definition) => {
                        if (isArray(definition)) {
                            definition.forEach((d2) => addObservable(d2));
                        }
                        else {
                            addObservable(definition);
                        }
                    });
                }
            });
        }
        else if (!componentConditions) {
            component.propertySet(property, true); // this case is only for the form designer
            return;
        }
        else {
            return;
        }
        if (envCondition || configCondition) {
            component.propertySet(property, !this.ConditionsManager.validateConditions(componentConditions, {
                form: this,
                initiator: component,
                record: this.records[recordName]
            }));
        }
        if (dataObservables.length) {
            combineLatest(dataObservables)
                .pipe(takeUntil(component.destroy$), debounceTime(dataObservables.length > 1 ? 10 : 0)).subscribe(() => {
                component.propertySet(property, !this.ConditionsManager.validateConditions(componentConditions, {
                    form: this,
                    initiator: component,
                    record: this.records[recordName]
                }));
            });
        }
    }
    addCompositeComponent(component) {
        // if (component.containerId && this.components[component.containerId] && !(this.components[component.containerId] as Container).componentGet(component.id)) {
        // 	// container will draw its children when applicable 
        // 	(this.components[component.containerId] as Container).componentAdd(component, (this.components[component.containerId] as Container).drawn);
        // }
        //
        forEachKey(component.componentsGet(), (childComponent) => {
            if (childComponent.omitFromFormDesigner) {
                return;
            }
            childComponent.formDesignerMode = this.formDesignerMode;
            if (childComponent.drawn) {
                childComponent.rootElement.classList.add('form-designer');
            }
            if (!this.componentGet(childComponent.id)) {
                childComponent.compositeChild = true;
                if (this.formDesignerMode) {
                    childComponent.defaultPropertiesObject = deepClone(childComponent.formDesignerPropertiesGet({}));
                    forEachKey(childComponent.defaultPropertiesObject, (prop, key) => {
                        const saveFlag = typeof prop.saveFlag === 'function' ? prop.saveFlag() : prop.saveFlag;
                        if (!saveFlag) {
                            delete prop[key];
                        }
                    });
                }
                this.componentAdd(childComponent, false);
            }
            if (childComponent instanceof Container && childComponent.compositeContainer) {
                this.addCompositeComponent(childComponent);
            }
        });
    }
    drawChildren() {
        const attachModals = !this.childrenDrawn;
        super.drawChildren();
        if (attachModals) {
            forEachKey(this.modals, (modal) => {
                if (!modal.rootElement) {
                    modal.rootElement = document.createElement(modal.rootElementType);
                    modal.rootElement.setAttribute('data-componentid', modal.id);
                }
                modal.attachModal(this.rootElement);
            });
        }
    }
    componentRemove(id, removeFromDOM = true) {
        super.componentRemove(id, removeFromDOM);
        if (this.modals && this.modals[id]) {
            delete this.modals[id];
        }
    }
    componentAdd(component, appendToDOM = false) {
        super.componentAdd(component, appendToDOM);
        if (component instanceof Modal && !(component instanceof FilterPanel)) {
            this.modals[component.id] = component;
        }
    }
    /**
     * Loads record properties, and calls form-specific setup function if it exists
     * @param recordDef
     * @private
     */
    recordSetup(recordDef) {
        const id = recordDef.recordName;
        const record = new DataRecord(id, { formGlobalId: this.globalId });
        recordDef.permCreate = recordDef.permCreate === 'Y';
        recordDef.permEdit = recordDef.permEdit === 'Y';
        recordDef.permDelete = recordDef.permDelete === 'Y';
        record.propertiesLoad(recordDef);
        this.records[id] = record;
        /*if (typeof (this as any)[`record_${id}`] === 'function') {
            // call form specific component setup code defined in CustomerForm.js etc
            (this as any)[`record_${id}`](record);
        }*/
        if (record.fkColumnName && this.records.main && this.records.main.recordType === 'primary') {
            // we need to setup an observable to reload this record if the fk changes
            this.records.main.get$(record.fkColumnName, { ignoreSource: ['load'], distinct: true })
                .subscribe((newValue) => {
                const secondaryRecord = this.recordGet(id);
                if (!secondaryRecord || secondaryRecord.pkValue === newValue) {
                    return;
                }
                console.log(`Record: ${id} needs to refresh as the fk column ${record.fkColumnName} just changed to ${newValue}`);
                this.refreshSecondaryRecord(id, newValue);
            });
        }
        return record;
    }
    refreshSecondaryRecord(id, pkValue) {
        const requestData = { ...this.parameters, ...{
                recordname: id,
                [`${id}_pk`]: pkValue,
                _q360formno: this.formNo,
                formbuilder: this.formDesignerMode ? 'Y' : 'N'
            } };
        const observable = this.Request.GET({
            endpoint: `/form/${this.formName}`,
            action: 'refreshData',
            loadingMessage: 'Loading Data...',
            showErrors: true,
            requestData: requestData,
            initiator: this.drawn ? this : undefined
        });
        observable
            .subscribe((response) => {
            if (response.payload[`record_${id}`]) {
                const record = this.records[id];
                const data = response.payload[`record_${id}`][0] || {};
                record.load(data);
                // if any filter records are synced up to this record, we need to inform them of the new value (the observable only handles values not from "load").
                forEachKey(this.recordLoadColumnMapping, (mappings, filterRecordName) => {
                    mappings.forEach((mapping) => {
                        if (mapping.recordName === id) {
                            this.records[filterRecordName].set(mapping.columnName, data[mapping.fromColumnName]);
                        }
                    });
                });
            }
        });
        return observable;
    }
    recordGet(id) {
        return this.records[id];
    }
    /**
     * Utility function to provide easy access to CaptionManager for form level code.
     * @param captionKey
     *
     * @param params
     * @example
     * form.getCaption('form_customer_prompt1_caption');
     */
    getCaption(captionKey, params) {
        return this.CaptionManager.getCaption(captionKey, params);
    }
    /**
     * Utility function to provide easy means to redirect a form's screen or open a new form.
     * If the provided url matches the forms current formname, it will refresh data instead.
     * @param url
     * @param parameters
     */
    navigate(url, parameters) {
        const { /*pathname ,*/ params, formname } = paramsFromUrlGet(url);
        if (parameters) {
            // params passed in explicitly will override the url. 
            parameters = { ...params, ...parameters };
        }
        else {
            parameters = params;
        }
        if (formname === this.formName || this.formDesignerMode) {
            // cant navigate away from the form if in the form builder
            // + this fixes for stuff like "custom_dispatch" or "tech_dispatch" form names not matching "formname" which should only be possible in the form builder
            // because you dont use the url /form/custom_dispatch or /form/tech_dispatch in the real client
            return this.refreshData(parameters).pipe(map(() => this), tap(() => this.focus()));
        }
        else {
            const screen = this.screenGet();
            if (screen) {
                return screen.navigate(url, parameters).pipe(tap(() => this.focus()));
            }
        }
        return new Observable(subscriber => {
            subscriber.error(new Error('Failed to navigate.'));
        });
    }
    recordNavigationDetailSet(_detail) {
    }
    /**
     * Utility function to provide the Screen containing this form.
     * @returns Screen | undefined
     */
    screenGet() {
        return DIContainer.getInstance('ScreenManager').screenGet(this.containerId);
    }
    handleResize() {
        if (!this.childContainer) {
            return;
        }
        if (!this.childrenDrawn) {
            return;
        }
        if (!this.rootElement.clientHeight) {
            return;
        }
        if (!this.components.actionbar || this.components.actionbar.hidden) {
            this.rootElement.classList.add('no-actionbar');
        }
        else {
            this.rootElement.classList.remove('no-actionbar');
        }
        let childContainer = this.childContainer;
        if (this.components.tabset_main) {
            childContainer = this.components.tabset_main.rootElement;
        }
        let query = '.panels:not(.d-none) .s360FilterPanel.docked:not(.d-none)';
        if (this.components.tabset_main) {
            query = ':scope > .s360TabSet > .tab-container > .s360Tab.active .panels:not(.d-none) .s360FilterPanel.docked:not(.d-none)';
        }
        const openPanels = this.childContainer.querySelectorAll(query);
        if (openPanels.length) {
            openPanels.forEach((openPanel) => {
                openPanel.classList.add('d-none');
            });
        }
        query = '.autoResize';
        if (this.components.tabset_main) {
            query = ':scope > .tab-container > .s360Tab.active .autoResize';
        }
        const formHeight = this.childContainer.clientHeight;
        containerAutoResize(childContainer, query, formHeight);
        if (openPanels.length) {
            openPanels.forEach((openPanel) => {
                openPanel.classList.remove('d-none');
                DIContainer.getInstance('ComponentRegistry').getComponentByElement(openPanel, false).handleResize();
            });
        }
    }
    /**
     * Event handler called when the form needs to adjust the size of child components.
     * Autosizes `Row`s containing components like Grid that have autoResize: true.
     */
    /*handleResizeOld() {
        if (!this.childContainer) {
            return;
        }

        // Find all rows that are direct children of active tabs
        let rows: HTMLDivElement[] = Array.from(
            this.childContainer.querySelectorAll(':scope > .s360TabSet > .tab-container > .s360Tab.active > .row, :scope > .s360TabSet > .tab-container > .s360Tab.active .s360Tab.active > .row') as NodeListOf<HTMLDivElement>
        );
        if (!rows.length) {
            // handle no tabset
            rows = Array.from(
                this.childContainer.querySelectorAll('.row') as NodeListOf<HTMLDivElement>
            );
        }

        let tabPadding = this.childContainer.querySelectorAll('.s360Tab.active').length * 15; // (padding top 5 and bottom 10)

        let tabButtonBars: HTMLDivElement[] = Array.from(this.childContainer.querySelectorAll('.s360TabSet.horizontal > .button-container') as NodeListOf<HTMLDivElement>);
        tabButtonBars.forEach(bar => {
            const style = window.getComputedStyle(bar);
            
            const marginTop = parseFloat(style.marginTop);
            const marginBottom = parseFloat(style.marginBottom);
            
            const elementHeight = bar.offsetHeight;
            
            tabPadding += elementHeight + marginTop + marginBottom;
        });
        
        // Get total available height from the child container
        const totalHeight = this.childContainer.clientHeight;

        // Calculate the total height of rows that do NOT contain any '.autoResize' elements
        let staticRowsHeight = rows.reduce((total, row) => {
            // Check if the row contains any autoResize elements
            let hasAutoResize = row.querySelector('.autoResize');
            if (!hasAutoResize) {
                // If no autoResize elements, add this row's height to the staticRowsHeight
                return total + row.offsetHeight;
            }
            return total;
        }, 0);

        // Subtract the static rows' height from the total container height to get the available height for autoResize rows
        const availableHeight = totalHeight - staticRowsHeight - tabPadding - 5;

        // Find rows that contain '.autoResize' elements
        let autoResizeRows = rows.filter(row => row.querySelector('.autoResize'));

        // Divide the available height equally among rows with autoResize components
        let rowHeight = availableHeight / autoResizeRows.length;
        
        let getElementOffsetTop = (element: HTMLElement, parentElement: HTMLElement): number => {
            let offsetTop = 0;
            
            // Loop through all the offset parents, accumulating their offsetTop values
            while (element !== parentElement) {
                offsetTop += element.offsetTop;
                element = element.parentElement as HTMLElement;
            }
            
            return offsetTop;
        }
        
        
        autoResizeRows.forEach(row => {
            // Get all autoResize elements within the current row
            let autoResizeElements: HTMLDivElement[] = Array.from(row.querySelectorAll('.autoResize') as NodeListOf<HTMLDivElement>);
            
            // Group autoResize elements by distinct top positions
            let groups: { [key: string]: HTMLDivElement[] } = {};
            
            autoResizeElements.forEach(element => {
                // Get the top offset of the element
                const topOffset = getElementOffsetTop(element, row);
                // const topOffset = element.getBoundingClientRect().top.toString();
                
                // If a group for this top offset doesn't exist, create one
                if (!groups[topOffset]) {
                    groups[topOffset] = [];
                }
                
                // Add the element to the group corresponding to its top offset
                groups[topOffset].push(element);
            });
            
            rowHeight = rowHeight / Object.keys(groups).length
            
            // Now you can process each group of elements
            forEachKey(groups, (groupElements, topOffset) => {
                let height = rowHeight - Number(topOffset);
                height = Math.max(height, 300);
                groupElements.forEach(element => {
                    let component = DIContainer.getInstance<ComponentRegistry>('ComponentRegistry').getComponentByElement(element, false);
                    if (component) {
                        component.height = `${height}px`;
                    }
                });
            })
        });
        
        
    }
    */
    // handleResizeOld() {
    // 	if (!this.childContainer) {
    // 		return;
    // 	}
    // 	debugger;
    // 	// 15 for padding 
    // 	let availableHeight = this.childContainer.clientHeight - 15;
    // 	let gridsToResize: Component[] = [];
    //	
    //	
    //	
    // 	// First loop: Collect grids that need resizing
    // 	for (let key in this.components) {
    // 		let component = this.components[key];
    //
    // 		// Check if the component's root element is visible and needs auto resize
    // 		if (component.rootElement && component.rootElement.offsetParent !== null && component.autoResize) {
    // 			gridsToResize.push(component);
    // 		}
    // 	}
    //
    // 	let rows: HTMLDivElement[] = Array.from(this.childContainer.querySelectorAll('.row') as NodeListOf<HTMLDivElement>);
    //
    // 	// Deduct the height of rows that don't contain grids to resize and are not nested inside other rows
    // 	rows.forEach(row => {
    // 		let containsGridToResize = false;
    // 		let isNestedRow = false;
    //
    // 		// Check if this row contains a grid that needs resizing
    // 		gridsToResize.forEach(grid => {
    // 			if (row.contains(grid.rootElement)) {
    // 				containsGridToResize = true;
    // 			}
    // 		});
    //
    // 		// Check if the row is nested inside another row
    // 		let parent = row.parentElement;
    // 		while (parent && parent !== this.childContainer) {
    // 			if (parent.classList.contains('row')) {
    // 				isNestedRow = true;
    // 				break;
    // 			}
    // 			parent = parent.parentElement;
    // 		}
    //
    // 		// If the row doesn't contain a grid to resize, is visible, and is not a nested row, deduct its height
    // 		if (!containsGridToResize && row.offsetParent !== null && !isNestedRow) {
    // 			availableHeight -= row.offsetHeight;
    // 		}
    // 	});
    //
    // 	let tabButtonBars: HTMLDivElement[] = Array.from(this.childContainer.querySelectorAll('.s360TabSet.horizontal .button-container.tabset') as NodeListOf<HTMLDivElement>);
    // 	tabButtonBars.forEach(bar => {
    // 		availableHeight -= bar.offsetHeight;
    // 	});
    //
    // 	// Handle side-by-side grids
    // 	let gridsByRow: Map<HTMLDivElement, Component[]> = new Map();
    //
    // 	rows.forEach(row => {
    // 		let rowGrids: Component[] = [];
    // 		gridsToResize.forEach(grid => {
    // 			if (row.contains(grid.rootElement)) {
    // 				rowGrids.push(grid);
    // 			}
    // 		});
    // 		if (rowGrids.length > 0) {
    // 			gridsByRow.set(row, rowGrids);
    // 		}
    // 	});
    //
    // 	gridsByRow.forEach((grids, row) => {
    // 		let rowHeight = availableHeight / gridsByRow.size;
    // 		rowHeight = Math.max(rowHeight, 300);
    // 		grids.forEach(grid => grid.height = `${rowHeight}px`);
    // 	});
    // }
    /**
     * Reloads the forms main record data.
     * Optionally accepts parameters that would match the forms expected query string parameters.
     * This would allow you to redirect the current record to another one via primary key.
     * @param parameters - {[key:string]: string}
     *
     * @example
     * form.refreshData();
     * form.refreshData({customerno: 'PRE001'});
     */
    refreshData(parameters) {
        this.cancelRefreshData$.next();
        console.log('Form Load Params: ', parameters);
        if (parameters) {
            // if they provided parameters use those, otherwise use old parameters.
            this.propertySet('parameters', parameters);
        }
        if (!this.loadExternalData) {
            if (this.record) {
                this.record.load(parameters || {});
                this.updateTitle();
                return of({ payload: { [this.record?.id]: [this.record?.data] } });
            }
            else {
                return of();
            }
        }
        const observable = this.Request.GET({
            endpoint: `/form/${this.formName}`,
            action: 'refreshData',
            loadingMessage: 'Loading Data...',
            showErrors: true,
            requestData: {
                _q360formno: this.formNo,
                formbuilder: this.formDesignerMode ? 'Y' : 'N',
                ...this.parameters
            },
            initiator: this
        });
        observable.pipe(takeUntil(this.cancelRefreshData$)).subscribe((response) => {
            const recordDataGet = (recordId) => {
                return response.payload[recordId] && response.payload[recordId].length ? response.payload[recordId][0] : {};
            };
            const hasTokens = {};
            for (const key in response.payload) {
                if (key.indexOf('record_') === 0) {
                    const recordName = key.substring(7);
                    let record = this.records[recordName];
                    if (!record) {
                        record = new DataRecord(recordName, { formGlobalId: this.globalId });
                        this.records[recordName] = record;
                    }
                    let data = recordDataGet(key);
                    if (Reflect.has(this.recordLoadColumnMapping, recordName)) {
                        this.recordLoadColumnMapping[recordName].forEach((mapping) => {
                            const responseData = recordDataGet('record_' + mapping.recordName);
                            const value = Reflect.has(responseData, mapping.fromColumnName)
                                ? responseData[mapping.fromColumnName]
                                : this.records[mapping.recordName].get(mapping.fromColumnName);
                            data[mapping.columnName] = value;
                        });
                    }
                    record.q360formrecordvalue?.forEach((recordValue) => {
                        if (this.initialLoadDone && recordValue.refreshmode === 'initial') {
                            data[recordValue.columnname] = record.get(recordValue.columnname);
                        }
                        else if (recordValue.refreshmode === 'parameterChange'
                            && JSON.stringify(this.lastParameters) === JSON.stringify(this.parameters)) {
                            data[recordValue.columnname] = record.get(recordValue.columnname);
                        }
                    });
                    for (const dataKey in data) {
                        if (typeof (data[dataKey] || '') === 'string'
                            && (data[dataKey] || '').startsWith('{{')
                            && (data[dataKey] || '').endsWith('}}')) {
                            hasTokens[recordName] = data;
                        }
                    }
                    if (!hasTokens[recordName]) {
                        if (record.recordType === 'detail') {
                            // merge because it might already have some linked data from its list mixin
                            data = { ...record.data, ...data };
                        }
                        record.load(data);
                    }
                }
            }
            // for tokens like {{main.customerno}}, where we want to grab the value from another record just once on refreshData, 
            // as opposed to ${main.customerno}$ which would observe the main record and update the sub-record whenever that column changes in realtime.
            for (const recordName in hasTokens) {
                let data = hasTokens[recordName];
                for (const dataKey in data) {
                    if ((data[dataKey] || '').indexOf('{{') > -1) {
                        data[dataKey] = this.recordStore.replaceTokens(data[dataKey], undefined, { recordTokensOnly: true, keepMissingTokens: true });
                    }
                }
                const record = this.records[recordName];
                if (record.recordType === 'detail') {
                    // merge because it might already have some linked data from its list mixin
                    data = { ...record.data, ...data };
                }
                record.load(data);
            }
            this.updateTitle();
            this.lastParameters = { ...this.parameters };
            this.initialLoadDone = true;
        });
        return observable;
    }
    setTabFromHash(hash) {
        hash = hash.replace('#', '');
        if (hash) {
            const tabs = hash.split('/');
            tabs.forEach((tabid) => {
                const tab = this.componentGet(tabid);
                if (tab) {
                    const tabset = this.componentGet(tab.containerId);
                    if (tabset) {
                        tabset.currentTabSet(tab.id);
                    }
                }
            });
        }
    }
    getHashFromCurrentTab() {
        const tabset = this.componentGet('tabset_main');
        let hash = '';
        if (tabset) {
            hash = tabset.currentTab?.id;
        }
        if (tabset && tabset.currentTab) {
            const root = tabset.currentTab.rootElement;
            if (root) {
                const subtabsetroot = root.querySelector('.s360TabSet');
                if (subtabsetroot && subtabsetroot.dataset.componentid) {
                    const subtabset = this.componentGet(subtabsetroot.dataset.componentid);
                    if (subtabset && subtabset.currentTab?.id) {
                        hash += '/' + subtabset.currentTab.id;
                    }
                }
            }
        }
        if (hash) {
            return '#' + hash;
        }
        return '';
    }
    getHashFromCurrentTab$() {
        const tabset = this.componentGet('tabset_main');
        if (tabset) {
            return tabset.propertyGet$('currentTab')
                .pipe(map((tab) => {
                return tab?.id ? '#' + tab?.id : '';
            }));
        }
        else {
            return of('');
        }
    }
    getCurrentUrl() {
        const baseUrl = ENV('baseUrl');
        const name = this.propertyGet('formName');
        const params = { ...(this.propertyGet('parameters') || {}) };
        const hash = this.getHashFromCurrentTab();
        for (const key in params) {
            if (typeof params[key] === 'undefined') {
                delete params[key];
            }
        }
        const queryString = new URLSearchParams(params).toString();
        return baseUrl + (name ? `/form/${name}` : '') + (queryString ? `?${queryString}` : '') + hash;
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        const whitelist = [
            'captionKey',
            'componentName',
            'title',
            'titleAdd',
            'titleView',
            'broadcastListenerConditions',
            'dynamicHandlers'
        ];
        forEachKey(properties, (property, propertyId) => {
            if (whitelist.indexOf(propertyId) === -1 && propertyId.indexOf('dynamicHandlers') === -1) {
                delete properties[propertyId];
            }
        });
        properties.formname = ({
            id: 'formname',
            value: this.formName,
            componentType: 'Input',
            componentProperties: {
                disabled: true
            },
            captionKey: 'q360form_formname',
            propertyGroup: '',
            propertyIndex: 2,
            saveFlag: false,
            handlePropertyChange: (newValue, oldValue, source) => {
                if (source === 'panel') {
                    this.formName = newValue;
                }
                return newValue;
            }
        });
        properties.parentFormName = ({
            id: 'parentFormName',
            value: this.parentFormName,
            componentType: 'Input',
            componentProperties: {
                disabled: true
            },
            captionKey: 'q360form_parentformname',
            propertyGroup: '',
            propertyIndex: 3,
            saveFlag: false,
            handlePropertyChange: (newValue, oldValue, source) => {
                if (source === 'panel') {
                    this.parentFormName = newValue;
                }
                return newValue;
            }
        });
        properties.abstractFlag = ({
            id: 'abstractFlag',
            value: this.abstractFlag,
            componentType: 'InputCheckbox',
            componentProperties: {},
            captionKey: 'q360form_abstractflag',
            propertyGroup: '',
            propertyIndex: 4,
            saveFlag: false,
            handlePropertyChange: (newValue, oldValue, source) => {
                if (source === 'panel') {
                    this.abstractFlag = newValue;
                }
                return newValue;
            }
        });
        properties.routeOnlyFlag = ({
            id: 'routeOnlyFlag',
            value: this.routeOnlyFlag,
            componentType: 'InputCheckbox',
            componentProperties: {},
            captionKey: 'q360form_routeonlyflag',
            propertyGroup: '',
            propertyIndex: 4,
            saveFlag: false,
            handlePropertyChange: (newValue, oldValue, source) => {
                if (source === 'panel') {
                    this.routeOnlyFlag = newValue;
                }
                return newValue;
            }
        });
        properties.q360formno = ({
            id: 'q360formno',
            value: this.formNo,
            componentType: 'Input',
            componentProperties: {
                disabled: true
            },
            captionKey: 'q360form_q360formno',
            propertyGroup: '',
            propertyIndex: 1,
            saveFlag: false
        });
        properties.formType = ({
            id: 'formType',
            value: this.propertyGet('formType') || 'Q360Form',
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: 'Form', display: 'Form' },
                    { value: 'Q360Form', display: 'Q360Form' },
                    { value: 'Q360MobileAppForm', display: 'Q360MobileAppForm' }
                ]
            },
            captionKey: 'formbuilder_formtype',
            propertyGroup: '',
            propertyIndex: 5,
            saveFlag: () => !!this.propertyGet('formType') && this.propertyGet('formType') !== 'Q360Form'
        });
        properties.condition = ({
            id: 'condition',
            value: this.viewCondition,
            componentType: 'ConditionsEditor',
            componentProperties: {
                labelPosition: 'top'
            },
            captionKey: 'formbuilder_viewcondition',
            propertyGroup: 'permissions',
            propertyIndex: 3,
            saveFlag: false,
            handlePropertyChange: (newValue, oldValue, source) => {
                if (source === 'panel') {
                    this.viewCondition = newValue;
                }
                return newValue;
            }
        });
        properties.broadcastListenerConditions = {
            id: 'broadcastListenerConditions',
            value: this.broadcastListenerConditions,
            componentType: 'InputGrid',
            componentProperties: {
                dataFormat: 'OBJECTMAP',
                dbGridName: 'BroadcastListenerConditions',
                includeAddButton: true,
                includeDeleteButton: true,
                labelPosition: 'top',
                grid: {
                    autoResize: false
                }
            },
            captionKey: 'formbuilder_form_broadcastlistenerconditions',
            propertyGroup: 'events',
            propertyIndex: 0,
            saveFlag: !!Object.keys(this.broadcastListenerConditions).length
        };
        return properties;
    }
    template() {
        const template = '<div class="form-content child-container"></div>';
        return template;
    }
    destroy() {
        this.cancelRefreshData$.next();
        this.cancelRefreshData$.complete();
        for (const key in this.records) {
            this.records[key].destroy();
            delete this.records[key];
        }
        // this.componentSubjects.forEach((subject) => subject.complete());
        // this.componentSubjects.clear();
        this._screenRootMoved = undefined;
        this.suppressedMandatoryIds.clear();
        for (const key in this.modals) {
            delete this.modals[key]; // its also in this.components so it will be destroyed in super code. 
        }
        super.destroy();
    }
    close() {
        this.handleFormClose().subscribe(() => this.screenGet()?.closeScreen());
    }
    handleFormClose() {
        return this.trigger('formclose', {
            defaultEventHandler: this.validateClose.bind(this),
        });
    }
    validateClose() {
        if (this.closePromptCaptionKey) {
            const screenCover = this.screenGet()?.parentComponentGet()?.screenCover;
            const tilesContainer = this.screenGet()?.parentComponentGet()?.tilesContainer;
            if (screenCover && screenCover.isConnected && tilesContainer) {
                // the tiles are up and we need to make them inaccessible
                tilesContainer.classList.add('disabled');
            }
            if (this.blockClose) {
                return DIContainer.getInstance('Application').alert({ captionKey: this.closePromptCaptionKey }).pipe(map(() => false));
            }
            else {
                return mapCancelToFalse(DIContainer.getInstance('Application').confirm({ captionKey: this.closePromptCaptionKey })).pipe(tap(confirmed => {
                    if (confirmed && this.record) {
                        // dont delay the form close, just unlock record async
                        this.record.unlockRecord$().subscribe();
                    }
                }));
            }
        }
        else {
            if (this.record) {
                // dont delay the form close, just unlock record async
                this.record.unlockRecord$().subscribe();
            }
            return of(true);
        }
    }
}
Form.VERSION = '$Revision: 111642 $';
